import { Flex, Segmented, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDate, orDash } from '../../lib/format';
import { bookingLabelKey, bookingTone } from './booking-status';
import { listBookings, type Booking } from './bookings-api';
import { formatMoney } from '../../lib/money';

const { Text } = Typography;

/**
 * "Lapsed" is a view, not a status. A booking past its 90 days is still
 * CONFIRMED and the bike is still reserved — nothing releases it automatically
 * (booking-policy.ts). This tab is how somebody notices in time to ring the
 * customer, which is the only thing that actually resolves it.
 */
type View = 'notReserved' | 'reserved' | 'lapsed' | 'all';

export function BookingListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>('all');

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['bookings', view, search], (page) =>
    listBookings({
      ...page,
      q: search,
      // OPEN: no bike reserved yet — both "No bike yet" and "Awaiting deposit".
      ...(view === 'notReserved' ? { status: 'OPEN' as const } : {}),
      ...(view === 'reserved' ? { status: 'CONFIRMED' as const } : {}),
      ...(view === 'lapsed' ? { lapsed: true } : {}),
    }),
  );

  const emptyFor: Record<View, string> = {
    notReserved: t('bookings.emptyNotReserved'),
    reserved: t('bookings.emptyReserved'),
    lapsed: t('bookings.emptyLapsed'),
    all: t('bookings.empty'),
  };

  return (
    <Flex vertical gap={12}>
      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'all', label: t('bookings.viewAll') },
          { value: 'notReserved', label: t('bookings.viewNotReserved') },
          { value: 'reserved', label: t('bookings.viewReserved') },
          { value: 'lapsed', label: t('bookings.viewLapsed') },
        ]}
      />

      <ListPage<Booking>
        title={t('bookings.title')}
        searchPlaceholder={t('bookings.search')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={search.length > 0 ? t('bookings.noResults') : emptyFor[view]}
        columns={[
          {
            title: t('bookings.fields.bookingNo'),
            dataIndex: 'bookingNo',
            width: 180,
            render: (value: string) => <span className="tabular">{value}</span>,
          },
          { title: t('sales.fields.customer'), dataIndex: 'customerName' },
          {
            title: t('sales.fields.status'),
            dataIndex: 'status',
            width: 170,
            render: (status: Booking['status'], row: Booking) => (
              <Flex gap={4} wrap>
                <Tag color={bookingTone(status, row.bikeTaken)}>{t(bookingLabelKey(row))}</Tag>
                {row.hasLapsed && <Tag color="error">{t('bookings.lapsed')}</Tag>}
              </Flex>
            ),
          },
          {
            title: t('bookings.fields.chassisNo'),
            dataIndex: 'chassisNo',
            width: 190,
            render: (value: string | null) => orDash(value),
          },
          {
            title: t('bookings.fields.deposit'),
            dataIndex: 'depositAmount',
            width: 150,
            align: 'right' as const,
            render: (value: string, row: Booking) => formatMoney(value, row.currency),
          },
          {
            title: t('bookings.fields.validUntil'),
            dataIndex: 'validUntil',
            width: 150,
            render: (value: string | null, row: Booking) =>
              value === null ? (
                '—'
              ) : (
                <Text type={row.hasLapsed ? 'danger' : undefined}>{formatDate(value)}</Text>
              ),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        onRowClick={(row) => {
          void navigate(`/bookings/${row.uid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={4}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong>{row.customerName}</Text>
              <Tag color={bookingTone(row.status, row.bikeTaken)} style={{ marginInlineEnd: 0 }}>
                {t(bookingLabelKey(row))}
              </Tag>
            </Flex>
            <Flex justify="space-between" gap={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.chassisNo ?? row.bookingNo}
              </Text>
              <Text strong>{formatMoney(row.depositAmount, row.currency)}</Text>
            </Flex>
          </Flex>
        )}
      />
    </Flex>
  );
}
